/**
 * 快递询价供应商报价前端接口
 */

import { request } from '@/utils/http'
import { type BatchDeleteRequest } from '@/api/types/common'
import {
  type InitiateConsultationRequest,
  type QuotationBaseModel,
  type QuotationSaveRequest
} from './quotation-base'

export interface ExpressFirstAdditional {
  firstWeightThreshold?: number | null
  firstWeightPrice?: number | null
  additionalWeightUnit?: number | null
  additionalWeightPrice?: number | null
  minCharge?: number | null
}

export interface ExpressUnitPrice {
  billingMethod?: string | null
  unitPrice?: number | null
  minCharge?: number | null
  pricingMethod?: string | null
  tripPrice?: number | null
  kmPrice?: number | null
  minKm?: number | null
}

export interface ExpressWeightTier {
  id?: string
  billingMethod?: string | null
  weightStart?: number | null
  weightEnd?: number | null
  unitPrice?: number | null
  additionalWeightPrice?: number | null
  minCharge?: number | null
  sortOrder?: number | null
}

export interface ExpressChargingItem {
  id?: string
  quotationId?: string
  chargeItemId?: string | null
  chargeCode?: string | null
  chargeName?: string | null
  feeSource?: number | null
  surchargeRuleId?: string | null
  billingMode?: string | null
  currency?: string | null
  paymentTerm?: string | null
  measure?: string | null
  measureNameCn?: string | null
  measureNameEn?: string | null
  unitRate?: number | null
  firstWeightThreshold?: number | null
  firstWeightPrice?: number | null
  additionalWeightUnit?: number | null
  additionalWeightPrice?: number | null
  quantity?: number | null
  minCharge?: number | null
  amount?: number | null
  isCountTotal?: boolean | null
  isTotalIncluded?: boolean | null
  remarks?: string | null
}

export interface ExpressCalculation {
  billingWeight?: number | null
  baseFreight?: number | null
  minCharge?: number | null
  chargeableBase?: number | null
  surchargeTotal?: number | null
  totalAmount?: number | null
  calculationWarnings?: string[] | null
  calculationComplete?: boolean | null
  currencyTotals?: ExpressCurrencyTotal[] | null
}

export interface ExpressCarrierWeightTierConfig {
  id?: string
  carrierId?: string
  serviceType?: string | null
  name?: string | null
  startWeight?: number | null
  endWeight?: number | null
  sort?: number | null
}

export interface ExpressCurrencyTotal {
  currency?: string | null
  baseFreight?: number | null
  surchargeTotal?: number | null
  percentageRate?: number | null
  totalAmount?: number | null
  complete?: boolean | null
}

export interface CargoItem {
  id?: string | number | null
  sortOrder?: number | null
  quantity?: number | null
  weight?: number | null
  cubage?: number | null
  length?: number | null
  width?: number | null
  height?: number | null
}

/** 快递匿名报价请求只传访问 Token，租户及授权范围均由服务端从 Token 中解析。 */
export interface LinkAccess {
  token: string
}

export interface QuotationExpressModel extends QuotationBaseModel {
  stuffingWarehouseId?: string | null
  validFrom?: string | null
  validTo?: string | null
  loadMode?: string | null
  routeType?: string | null
  expressCarrierId?: string | null
  inquiryExpressCarrierId?: string | null
  serviceType?: string | null
  onePriceSupported?: boolean | null
  cargoType?: string | number | null
  cargoDescription?: string | null
  needPickup?: boolean | null
  needDelivery?: boolean | null
  needUpstairs?: boolean | null
  needLoadingUnloading?: boolean | null
  expectedVehicleType?: string | null
  expectedTransitTime?: string | null
  pricingPreference?: string | null
  billingModel?: string | null
  billingMethod?: string | null
  volumetricFactor?: number | null
  currency?: string | null
  paymentTerm?: string | null
  inquiryPaymentTerm?: string | null
  minBillingWeight?: number | null
  pickupFee?: number | null
  deliveryFee?: number | null
  loadingFee?: number | null
  vehicleType?: string | null
  vehicleCapacity?: number | null
  vehicleVolume?: number | null
  pricingMethod?: string | null
  tripPrice?: number | null
  kmPrice?: number | null
  minKm?: number | null
  onePrice?: boolean | null
  onePriceAmount?: number | null
  onePriceCurrency?: string | null
  onePriceIncludedItems?: string | null
  onePriceRemarks?: string | null
  billingWeight?: number | null
  baseFreight?: number | null
  minCharge?: number | null
  chargeableBase?: number | null
  surchargeTotal?: number | null
  totalAmount?: number | null
  billingRemark?: string | null
  importedPriceId?: string | null
  importedDate?: string | null
  firstAdditional?: ExpressFirstAdditional | null
  unitPrice?: ExpressUnitPrice | null
  weightTiers?: ExpressWeightTier[]
  chargingItems?: ExpressChargingItem[]
  calculation?: ExpressCalculation | null
  cargoList?: CargoItem[]
}

export type QuotationExpressSaveRequest = QuotationSaveRequest & Omit<QuotationExpressModel, keyof QuotationBaseModel>

export const getQuotationExpressById = (id: string) => {
  return request.post<QuotationExpressModel>('/price/quotation/express/get', { id })
}

export const queryQuotationExpressByInquiry = (params: { inquiryIds?: Array<string | number>; inquiryNo?: string | null }) => {
  return request.post<QuotationExpressModel[]>('/price/quotation/express/queryByInquiry', params)
}

export const createQuotationExpressScheme = (quotationId: string | number) => {
  return request.post<string | number>(`/price/quotation/express/createScheme/${quotationId}`)
}

export const saveOpenQuotationExpress = (params: QuotationExpressSaveRequest) => {
  return request.post<string>('/price/quotation/express/save-open', params)
}

export const updateQuotationExpress = (params: QuotationExpressSaveRequest) => {
  return request.post<boolean>('/price/quotation/express/update', params)
}

export const batchSubmitQuotationExpress = (params: { quotations: QuotationExpressSaveRequest[] }) => {
  return request.post<boolean>('/price/quotation/express/batchSubmit', params)
}

export const batchDeleteQuotationExpress = (params: BatchDeleteRequest) => {
  return request.post<boolean>('/price/quotation/express/batch-delete', params)
}

export const initiateConsultationExpress = (params: InitiateConsultationRequest) => {
  return request.post<boolean>('/price/quotation/express/initiateConsultation', params)
}

export const importExpressQuotationToRate = (params: { id: string }) => {
  return request.post<string>('/price/quotation/express/import-price', params)
}

export const getAnonymousQuotationExpressById = (id: string | number, access: LinkAccess) => {
  return request.post<QuotationExpressModel>('/price/quotation/express/anonymous/get', {
    id,
    token: access.token
  })
}

export const queryAnonymousQuotationExpressByInquiry = (
  params: { inquiryIds?: Array<string | number>; inquiryNo?: string | null },
  access: LinkAccess
) => {
  return request.post<QuotationExpressModel[]>('/price/quotation/express/anonymous/queryByInquiry', {
    ...params,
    token: access.token
  })
}

export const queryAnonymousQuotationExpressWeightTiers = (
  params: { carrierId: string | number; serviceType: string },
  access: LinkAccess
) => {
  return request.post<ExpressCarrierWeightTierConfig[]>('/price/quotation/express/anonymous/weightTiers', {
    ...params,
    token: access.token
  })
}

export const createAnonymousQuotationExpressScheme = (
  quotation: QuotationExpressSaveRequest,
  access: LinkAccess
) => {
  return request.post<string | number>('/price/quotation/express/anonymous/createScheme', {
    quotation,
    token: access.token
  })
}

export const updateAnonymousQuotationExpress = (
  quotation: QuotationExpressSaveRequest,
  access: LinkAccess
) => {
  return request.post<boolean>('/price/quotation/express/anonymous/update', {
    quotation,
    token: access.token
  })
}

export const batchSubmitAnonymousQuotationExpress = (
  params: { quotations: QuotationExpressSaveRequest[] },
  access: LinkAccess
) => {
  return request.post<boolean>('/price/quotation/express/anonymous/batchSubmit', {
    ...params,
    token: access.token
  })
}

export const batchDeleteAnonymousQuotationExpress = (
  params: BatchDeleteRequest,
  access: LinkAccess
) => {
  return request.post<boolean>('/price/quotation/express/anonymous/batchDelete', {
    ...params,
    token: access.token
  })
}
